import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from 'api'
import { useAuth } from 'context/AuthContext'
import { parseError } from 'utils/error-handler'
import {
    CountryCode,
    FavritApp,
    PaymentMethodCategory,
    PaymentMethodConfiguration,
    PaymentMethodOverridableConfiguration,
    PaymentProvider,
    PayoutSource,
} from '../../generated-api'

export const usePaymentMethods = () => {
    const { getAccessToken } = useAuth()

    const query = useQuery(['payment-methods'], async () =>
        apiClient.PaymentMethodControllerApi.getPaymentMethods({
            headers: { Authorization: `Bearer ${await getAccessToken()}` },
        }).catch(parseError)
    )

    return query
}

export const usePaymentMethodDetails = (paymentProvider: PaymentProvider, paymentMethod: string) => {
    const { getAccessToken } = useAuth()

    const query = useQuery(['payment-method-details', paymentProvider, paymentMethod], async () =>
        apiClient.PaymentMethodControllerApi.getPaymentMethodDetails(
            {
                paymentProvider: paymentProvider,
                paymentMethod: paymentMethod,
            },
            {
                headers: { Authorization: `Bearer ${await getAccessToken()}` },
            }
        ).catch(parseError)
    )

    return query
}

export const useCreatePaymentMethodCountryConnection = (
    paymentProvider: PaymentProvider,
    paymentMethod: string,
    countryCode: CountryCode
) => {
    const { getAccessToken } = useAuth()
    const queryClient = useQueryClient()

    const createCountryConnection = async (countryConnectionDto: {
        paymentsEnabled: boolean
        defaultEnabledInApps: Set<FavritApp>
    }) =>
        apiClient.PaymentMethodControllerApi.createPaymentMethodCountryConnection(
            {
                paymentProvider,
                paymentMethod,
                createPaymentMethodCountryConnectionRequest: {
                    countryCode,
                    paymentsEnabled: countryConnectionDto.paymentsEnabled,
                    defaultEnabledInApps: countryConnectionDto.defaultEnabledInApps,
                },
            },
            {
                headers: {
                    Authorization: `Bearer ${await getAccessToken()}`,
                    'Content-Type': 'application/json',
                },
            }
        ).catch(parseError)

    return useMutation(createCountryConnection, {
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['payment-method-country-connections', paymentProvider, paymentMethod, countryCode],
            })

            queryClient.invalidateQueries({ queryKey: ['payment-method-countries', paymentProvider, paymentMethod] })
        },
    })
}

export const usePaymentMethodCountryConnections = (
    paymentProvider: PaymentProvider,
    paymentMethod: string,
    countryCode: CountryCode
) => {
    const { getAccessToken } = useAuth()

    const query = useQuery(
        ['payment-method-country-connections', paymentProvider, paymentMethod, countryCode],
        async () =>
            apiClient.PaymentMethodControllerApi.getPaymentMethodCountryConnections(
                {
                    paymentProvider: paymentProvider,
                    paymentMethod: paymentMethod,
                    countryCode: countryCode,
                },
                {
                    headers: { Authorization: `Bearer ${await getAccessToken()}` },
                }
            ).catch(parseError)
    )

    return query
}

export const useCreatePaymentMethodLocationConnection = (
    paymentProvider: PaymentProvider,
    paymentMethod: string,
    locationId: number
) => {
    const { getAccessToken } = useAuth()
    const queryClient = useQueryClient()

    const createLocationConnection = async (locationConnectionDto: {
        locationId: number
        enabledInApps: Set<FavritApp>
        overridableConfiguration?: PaymentMethodOverridableConfiguration
    }) =>
        apiClient.PaymentMethodControllerApi.createPaymentMethodLocationConnection(
            {
                paymentProvider,
                paymentMethod,
                createPaymentMethodLocationConnectionRequest: {
                    locationId,
                    enabledInApps: locationConnectionDto.enabledInApps,
                    overridableConfiguration: locationConnectionDto.overridableConfiguration,
                },
            },
            {
                headers: {
                    Authorization: `Bearer ${await getAccessToken()}`,
                    'Content-Type': 'application/json',
                },
            }
        ).catch(parseError)

    return useMutation(createLocationConnection, {
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['payment-method-location-connections', paymentProvider, paymentMethod, locationId],
            })

            queryClient.invalidateQueries({ queryKey: ['payment-method-locations', paymentProvider, paymentMethod] })
        },
    })
}

export const usePaymentMethodLocationConnections = (
    paymentProvider: PaymentProvider,
    paymentMethod: string,
    locationId: number
) => {
    const { getAccessToken } = useAuth()

    const query = useQuery(
        ['payment-method-location-connections', paymentProvider, paymentMethod, locationId],
        async () =>
            apiClient.PaymentMethodControllerApi.getPaymentMethodLocationConnections(
                {
                    paymentProvider: paymentProvider,
                    paymentMethod: paymentMethod,
                    locationId: locationId,
                },
                {
                    headers: { Authorization: `Bearer ${await getAccessToken()}` },
                }
            ).catch(parseError)
    )

    return query
}

export const usePaymentMethodCountries = (paymentProvider: PaymentProvider, paymentMethod: string) => {
    const { getAccessToken } = useAuth()

    const query = useQuery(['payment-method-countries', paymentProvider, paymentMethod], async () =>
        apiClient.PaymentMethodControllerApi.getPaymentMethodCountries(
            {
                paymentProvider: paymentProvider,
                paymentMethod: paymentMethod,
            },
            {
                headers: { Authorization: `Bearer ${await getAccessToken()}` },
            }
        ).catch(parseError)
    )

    return query
}

export const usePaymentMethodLocations = (paymentProvider: PaymentProvider, paymentMethod: string) => {
    const { getAccessToken } = useAuth()

    const query = useQuery(['payment-method-locations', paymentProvider, paymentMethod], async () =>
        apiClient.PaymentMethodControllerApi.getPaymentMethodLocations(
            {
                paymentProvider: paymentProvider,
                paymentMethod: paymentMethod,
            },
            {
                headers: { Authorization: `Bearer ${await getAccessToken()}` },
            }
        ).catch(parseError)
    )

    return query
}

export const useAllLocations = () => {
    const { getAccessToken } = useAuth()

    const query = useQuery(['all-locations'], async () =>
        apiClient.PaymentMethodControllerApi.getAllLocations({
            headers: { Authorization: `Bearer ${await getAccessToken()}` },
        }).catch(parseError)
    )

    return query
}

export const useEnsurePaymentMethodForLocation = (paymentProvider: PaymentProvider, paymentMethod: string) => {
    const { getAccessToken } = useAuth()
    const queryClient = useQueryClient()

    const ensurePaymentMethodForLocation = async (locationConnectionDto: {
        locationId: number
        enabledInApps?: Set<FavritApp>
        overridableConfiguration?: PaymentMethodOverridableConfiguration
    }) =>
        apiClient.PaymentMethodControllerApi.ensurePaymentMethodForLocation1(
            {
                locationId: locationConnectionDto.locationId,
                paymentProvider: paymentProvider,
                paymentMethod: paymentMethod,
                ensurePaymentMethodForLocationRequest: {
                    enabledInApps: locationConnectionDto.enabledInApps,
                    overridableConfiguration: locationConnectionDto.overridableConfiguration,
                    authenticatedUserEmail: 'system',
                },
            },
            {
                headers: {
                    Authorization: `Bearer ${await getAccessToken()}`,
                    'Content-Type': 'application/json',
                },
            }
        ).catch(parseError)

    return useMutation({
        mutationFn: ensurePaymentMethodForLocation,
        onSuccess: () => {
            queryClient.invalidateQueries(['payment-method-locations', paymentProvider, paymentMethod])
            queryClient.invalidateQueries(['payment-method-location-connections', paymentProvider, paymentMethod])
        },
    })
}

export const useDeactivatePaymentMethodForLocation = (
    paymentProvider: PaymentProvider,
    paymentMethod: string,
    locationId: number
) => {
    const { getAccessToken } = useAuth()
    const queryClient = useQueryClient()

    const deactivatePaymentMethodForLocation = async () =>
        apiClient.PaymentMethodControllerApi.deactivatePaymentMethodForLocation(
            {
                locationId,
                paymentProvider,
                paymentMethod,
            },
            {
                headers: {
                    Authorization: `Bearer ${await getAccessToken()}`,
                },
            }
        ).catch(parseError)

    return useMutation({
        mutationFn: deactivatePaymentMethodForLocation,
        onSuccess: () => {
            queryClient.invalidateQueries(['payment-method-locations', paymentProvider, paymentMethod])
            queryClient.invalidateQueries(['payment-method-location-connections', paymentProvider, paymentMethod])
        },
    })
}

export const useCreatePaymentMethod = () => {
    const { getAccessToken } = useAuth()
    const queryClient = useQueryClient()

    const createPaymentMethod = async (request: {
        paymentProvider: PaymentProvider
        paymentMethod: string
        paymentMethodCategory: PaymentMethodCategory
        payoutSource: PayoutSource
        _configuration: PaymentMethodConfiguration
    }) =>
        apiClient.PaymentMethodControllerApi.createPaymentMethod(
            {
                createPaymentMethodRequest: request,
            },
            {
                headers: {
                    Authorization: `Bearer ${await getAccessToken()}`,
                    'Content-Type': 'application/json',
                },
            }
        ).catch(parseError)

    return useMutation({
        mutationFn: createPaymentMethod,
        onSuccess: () => {
            queryClient.invalidateQueries(['payment-methods'])
        },
    })
}
